import type { ScheduleCardStatus } from "../types/scheduleCard";
import { canCreateCardFromSchedule } from "./scheduleSearchParams";

export type ScheduleCardFlagSource = {
  id?: number;
  has_card?: boolean;
  card_id?: number | null;
  can_create_card?: boolean;
  status?: number;
};

function asBooleanFlag(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return undefined;
}

function asOptionalId(value: unknown): number | null {
  if (value == null || value === "") return null;
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}

export function extractCardFlagsFromRecord(raw: unknown): ScheduleCardFlagSource {
  if (!raw || typeof raw !== "object") return {};

  const item = raw as Record<string, unknown>;
  const schedule =
    item.schedule && typeof item.schedule === "object"
      ? (item.schedule as Record<string, unknown>)
      : {};

  const hasCard = asBooleanFlag(item.has_card) ?? asBooleanFlag(schedule.has_card);
  const canCreate =
    asBooleanFlag(item.can_create_card) ?? asBooleanFlag(schedule.can_create_card);
  const cardId = asOptionalId(item.card_id) ?? asOptionalId(schedule.card_id);
  const id = asOptionalId(item.id) ?? asOptionalId(schedule.id) ?? undefined;

  return {
    id: id ?? undefined,
    has_card: hasCard,
    card_id: cardId,
    can_create_card: canCreate,
  };
}

export function resolveScheduleCardStatus(
  item?: ScheduleCardFlagSource | null
): ScheduleCardStatus {
  if (!item) {
    return { has_card: false, card_id: null, can_create_card: false };
  }

  const hasCard = Boolean(item.has_card);
  const cardId = item.card_id != null ? Number(item.card_id) : null;

  return {
    has_card: hasCard,
    card_id: cardId,
    can_create_card: canCreateCardFromSchedule({
      can_create_card: item.can_create_card,
      has_card: hasCard,
      status: item.status,
    }),
  };
}

function isAppointmentList(value: unknown): value is unknown[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.some((item) => {
    if (!item || typeof item !== "object") return false;
    const record = item as Record<string, unknown>;
    return (
      "schedule" in record ||
      "has_card" in record ||
      "can_create_card" in record ||
      "client" in record
    );
  });
}

export function extractScheduleDetailAppointments(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const root = payload as Record<string, unknown>;
  const nestedData =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : undefined;
  const original =
    root.original && typeof root.original === "object"
      ? (root.original as Record<string, unknown>)
      : undefined;

  const candidates = [root.data, original?.data, nestedData?.original].flatMap(
    (candidate) => {
      if (isAppointmentList(candidate)) return [candidate];
      if (
        candidate &&
        typeof candidate === "object" &&
        !Array.isArray(candidate) &&
        isAppointmentList((candidate as Record<string, unknown>).data)
      ) {
        return [(candidate as Record<string, unknown>).data as unknown[]];
      }
      return [];
    }
  );

  const withFlags = candidates.find((list) =>
    list.some((item) => {
      const flags = extractCardFlagsFromRecord(item);
      return (
        typeof flags.has_card === "boolean" ||
        typeof flags.can_create_card === "boolean"
      );
    })
  );

  return withFlags ?? candidates[0] ?? [];
}

export function cardStatusFromAppointment(raw: unknown): ScheduleCardStatus {
  return resolveScheduleCardStatus(extractCardFlagsFromRecord(raw));
}
